import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApp } from "@/context/AppContext";
import { FALLBACK_FACTS, type DidYouKnowFact } from "@/hooks/useDidYouKnow";
import { useTranslation } from "react-i18next";

export function LoadingScreen() {
  const { t } = useTranslation();
  const { lang } = useApp();
  const qc = useQueryClient();

  const facts: DidYouKnowFact[] =
    qc.getQueryData<DidYouKnowFact[]>(['did-you-know']) ?? FALLBACK_FACTS;

  const [idx, setIdx] = useState(() => Math.floor(Math.random() * facts.length));

  useEffect(() => {
    const id = setInterval(() => {
      setIdx(i => (i + 1) % facts.length);
    }, 4000);
    return () => clearInterval(id);
  }, [facts.length]);

  const fact = facts[idx];
  const text = fact?.[lang] || fact?.fr || "";

  return (
    <div
      data-testid="loading-screen"
      className="fixed inset-0 flex flex-col items-center justify-center bg-[#1E6B3F] px-8"
    >
      <img
        src="/icons/Mombongo_COOP_loading_logo.svg"
        alt="Mombongo"
        className="h-28 w-auto object-contain"
      />

      {/* Did you know */}
      {text && (
        <div className="mt-10 max-w-xs text-center">
          <p className="text-[10px] font-black text-white/50 uppercase tracking-[0.2em] mb-2">
            {t("loading.didYouKnow")}
          </p>
          <p
            key={idx}
            className="text-[14px] text-white/85 leading-relaxed font-medium animate-fade-in"
          >
            {text}
          </p>
        </div>
      )}

      {/* Dot pagination */}
      {facts.length > 1 && (
        <div className="flex gap-1.5 mt-5">
          {facts.map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-500 ${
                i === idx ? "w-5 h-1.5 bg-white" : "w-1.5 h-1.5 bg-white/30"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Inline version shown in the content area during navigation (shell stays visible)
export function PageLoader() {
  const { t } = useTranslation();
  const { lang } = useApp();
  const qc = useQueryClient();

  const facts: DidYouKnowFact[] =
    qc.getQueryData<DidYouKnowFact[]>(['did-you-know']) ?? FALLBACK_FACTS;

  const [idx, setIdx] = useState(() => Math.floor(Math.random() * facts.length));

  useEffect(() => {
    const id = setInterval(() => setIdx(i => (i + 1) % facts.length), 4000);
    return () => clearInterval(id);
  }, [facts.length]);

  const fact = facts[idx];
  const text = fact?.[lang] || fact?.fr || "";

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-8 py-12">
      <img
        src="/icons/Mombongo_COOP_loading_logo.svg"
        alt="Mombongo"
        className="h-24 w-auto object-contain"
      />

      {text && (
        <div className="mt-8 max-w-xs text-center">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">
            {t("loading.didYouKnow")}
          </p>
          <p key={idx} className="text-[13px] text-gray-600 leading-relaxed font-medium animate-fade-in">
            {text}
          </p>
        </div>
      )}

      {facts.length > 1 && (
        <div className="flex gap-1.5 mt-5">
          {facts.map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-500 ${
                i === idx ? "w-5 h-1.5 bg-green-700" : "w-1.5 h-1.5 bg-gray-200"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default LoadingScreen;
